import { DOCUMENT } from '@angular/common';
import { inject } from '@angular/core';

const STEPPING_KEYS = ['ArrowUp', 'ArrowDown'];

// Checked at event time rather than by selector, so inputs whose type is bound ([type]="...")
// are covered too.
function isNumberInput(target: EventTarget | null): target is HTMLInputElement {
  return target instanceof HTMLInputElement && target.type === 'number';
}

// Registered with provideAppInitializer. Number inputs across the application keep their
// numeric keyboard and their min/max/step validation, but the value can only be typed -
// never stepped. The native spinner itself is hidden in styles/components/_form-elements-resets.scss.
export function disableNumberInputSteppingFactory(): void {
  const document = inject(DOCUMENT);

  // Capture phase, so a handler calling stopPropagation() cannot bypass it. preventDefault
  // only cancels the browser's stepping - other arrow key handlers still run.
  document.addEventListener('keydown', (event: KeyboardEvent) => {
    if (STEPPING_KEYS.includes(event.key) && isNumberInput(event.target)) {
      event.preventDefault();
    }
  }, { capture: true });

  // The wheel listener lives on the focused input only, for two reasons: merely hovering a
  // number input must not block page scroll, and a non-passive wheel listener on document
  // would force main-thread scrolling for the whole application.
  const blockWheelStepping = (event: WheelEvent) => event.preventDefault();

  document.addEventListener('focusin', (event: FocusEvent) => {
    const target = event.target;
    if (isNumberInput(target)) {
      target.addEventListener('wheel', blockWheelStepping, { passive: false });
    }
  });

  document.addEventListener('focusout', (event: FocusEvent) => {
    const target = event.target;
    if (isNumberInput(target)) {
      target.removeEventListener('wheel', blockWheelStepping);
    }
  });
}
